import { apiFirst, apiListAll, apiPatch } from '@/core/api/resources'
import { testdatenDatei } from '@/core/api/testdaten'
import { assertWriteIdentity, captureWriteIdentity, type WriteIdentity } from '@/core/api/http'
import { me } from '@/core/data/session'
import {
  collectVisibleShellLeafFelder,
  dropHiddenShellWerte,
  isShellFeldEmpty,
  shellFelder,
} from '@/core/components/form/formModel'
import { friendlyErrorMessage } from '@/core/lib/dbError'
import type {
  Shell,
  ShellFeld,
  ShellFreitextAntwort,
  ShellOption,
  ShellQuellen,
  ShellWert,
  ShellWerte,
} from '@/core/templates/types'
import { AUFGABE, type AufgabeKey } from '../aufgabe'
import { anlegenTeilnehmendePerson } from '../api/zuweisungsliste'
import { verbisUeberspringen } from '../api/verbisLebenslauf'
import { GEGENZEICHNUNG, gegenzeichnen, openTeilnehmerakte } from '../api/teilnehmerakte-staff'
import { abschliessen, logoutToken, saveBlatt, signBlatt, startPortalSession } from '../api/portal'
import { getAufnahmePotenzialanalyse, saveAufnahmePotenzialanalyse } from '../api/aufnahmePotenzialanalyse'
import { uploadGeva, uploadKlett } from '../api/gevaKlett'
import { getBewertungsbogen, saveBewertungsbogen, type Bogen } from '../api/bewertungsbogen'
import { erzeugeKompetenzbilanzierung, speichereKompetenzbilanzierung } from '../api/kompetenzbilanzierung'
import { getFoerderplan, saveFoerderplan } from '../api/foerderplan'
import { unterschreibe, type UnterschriftDokument, type Unterschreibende } from '../api/unterschrift'
import { ABSATZ_KEYS, bestaetigeErstbericht, speichereErstberichtEntwurf } from '../api/erstbericht'

const TEXT = 'Arbeit im Lager'
const DATUM = '2030-01-01'

function antwort(option: ShellOption | undefined, feld: ShellFeld): string | ShellFreitextAntwort {
  if (option === undefined) throw new Error(`Das Feld „${feld.label}“ hat keine Antwort zur Auswahl.`)
  return typeof option === 'string' ? option : { label: option.label, text: TEXT }
}

function ersteAntwort(feld: ShellFeld): ShellWert {
  switch (feld.art) {
    case 'auswahl':
      return antwort(feld.optionen?.[0], feld)
    case 'mehrfach':
      return [antwort((feld.optionen ?? feld.gruppen?.[0]?.optionen)?.[0], feld)]
    case 'datum':
      return DATUM
    case 'zahl':
      return 0
    default:
      return TEXT
  }
}

export function pflichtfelderAusfuellen(shell: Shell, werte: ShellWerte, quellen: ShellQuellen = {}): ShellWerte {
  let aktuell = { ...werte }
  for (let runde = 0; runde < 500; runde++) {
    const offen = collectVisibleShellLeafFelder(shellFelder(shell), aktuell, quellen).find(
      (feld) => feld.pflicht === true && isShellFeldEmpty(feld, aktuell[feld.id]),
    )
    if (!offen) return dropHiddenShellWerte(shell, aktuell, quellen)
    aktuell = { ...aktuell, [offen.id]: ersteAntwort(offen) }
  }
  throw new Error(`„${shell.titel}“: ein Pflichtfeld bleibt nach jeder Antwort offen.`)
}

type Karte = { coachingId: string; vorname: string; nachname: string; identity: WriteIdentity }

type Zubehoer = {
  unterschrift: () => Promise<string>
  klett: () => Promise<File>
  geva: () => Promise<File>
}

function einmal<T>(laden: () => Promise<T>): () => Promise<T> {
  let wert: Promise<T> | null = null
  return () => (wert ??= laden())
}

async function pngBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer())
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary)
}

function zubehoer(identity: WriteIdentity): Zubehoer {
  return {
    unterschrift: einmal(async () => pngBase64(await testdatenDatei('unterschrift.png', identity))),
    klett: einmal(() => testdatenDatei('klett-ergebnis.pdf', identity)),
    geva: einmal(() => testdatenDatei('geva-dossier.pdf', identity)),
  }
}

async function teilnehmerakteAusfuellen(karte: Karte, z: Zubehoer): Promise<void> {
  const signatur = await z.unterschrift()
  assertWriteIdentity(karte.identity)
  const geoeffnet = await openTeilnehmerakte(karte.coachingId)
  assertWriteIdentity(karte.identity)
  const sessionId = startPortalSession(geoeffnet)
  try {
    const speichern = async (blatt: string, daten: Record<string, unknown>) => {
      assertWriteIdentity(karte.identity)
      if ((await saveBlatt(blatt, daten, sessionId)) !== 'gespeichert') {
        throw new Error(`Blatt ${blatt} ist schon unterschrieben.`)
      }
    }
    await speichern('04.0', {
      korrekturen: {
        anrede: 'frau',
        familienname: karte.nachname,
        vorname: karte.vorname,
        strasse: 'Teststraße 1',
        plz: '44135',
        wohnort: 'Dortmund',
        geburtsdatum: '1990-01-01',
        geburtsort: 'Dortmund',
        staatsangehoerigkeit: 'deutsch',
      },
    })
    await speichern('04.4', {
      eigener_name: `${karte.vorname} ${karte.nachname}`,
      kontakt_name: 'Erika Beispiel',
      mobiltelefon: '0170 0000000',
    })
    await speichern('04.5', {
      einwilligung: 'einverstanden',
      geraete: { computer: 'nein', laptop: 'ja', tablet: 'nein', smartphone: 'ja', internet: 'ja' },
    })
    await speichern('04.7', { kenntnisnahme: true, foto_einwilligung: false, widerruf_bekannt: true })
    for (const blatt of ['04.2', '04.3', '04.5', '04.6', '04.7']) {
      assertWriteIdentity(karte.identity)
      await signBlatt(blatt, signatur, sessionId)
    }
    assertWriteIdentity(karte.identity)
    const ende = await abschliessen(sessionId)
    if (!ende.ok) throw new Error(`Die Teilnehmerakte lässt sich nicht abschließen, es fehlt: ${ende.fehlend.join(', ')}.`)
  } finally {
    await logoutToken(geoeffnet.token, sessionId)
  }
}

async function teilnehmerakteGegenzeichnen(karte: Karte, z: Zubehoer): Promise<void> {
  const signatur = await z.unterschrift()
  assertWriteIdentity(karte.identity)
  const geoeffnet = await openTeilnehmerakte(karte.coachingId)
  try {
    for (const blatt of Object.keys(GEGENZEICHNUNG)) {
      assertWriteIdentity(karte.identity)
      await gegenzeichnen(karte.coachingId, blatt, signatur)
    }
  } finally {
    await logoutToken(geoeffnet.token)
  }
}

async function potenzialanalyseSpeichern(karte: Karte): Promise<void> {
  const stand = await getAufnahmePotenzialanalyse(karte.coachingId)
  const werte = pflichtfelderAusfuellen(stand.schale as Shell, stand.werte as ShellWerte, stand.quellen as ShellQuellen)
  assertWriteIdentity(karte.identity)
  await saveAufnahmePotenzialanalyse(karte.coachingId, werte, true)
}

function bewertungsbogenSpeichern(bogen: Bogen) {
  return async (karte: Karte): Promise<void> => {
    const stand = await getBewertungsbogen(karte.coachingId, bogen)
    const werte = pflichtfelderAusfuellen(stand.schale as Shell, stand.werte as ShellWerte, stand.quellen as ShellQuellen)
    assertWriteIdentity(karte.identity)
    await saveBewertungsbogen(karte.coachingId, bogen, werte)
  }
}

async function kompetenzbilanzierungSpeichern(karte: Karte): Promise<void> {
  assertWriteIdentity(karte.identity)
  const abschnitte = await erzeugeKompetenzbilanzierung(karte.coachingId)
  assertWriteIdentity(karte.identity)
  await speichereKompetenzbilanzierung(karte.coachingId, abschnitte)
}

async function foerderplanSpeichern(karte: Karte): Promise<void> {
  const stand = await getFoerderplan(karte.coachingId)
  const werte = pflichtfelderAusfuellen(stand.schale as Shell, stand.werte as ShellWerte, stand.quellen as ShellQuellen)
  assertWriteIdentity(karte.identity)
  await saveFoerderplan(karte.coachingId, werte)
}

async function erstberichtBestaetigen(karte: Karte): Promise<void> {
  const bearbeitung = {
    absaetze: Object.fromEntries(
      ABSATZ_KEYS.map((key) => [key, `Getippter Text der Testdaten zu ${key.replace('_', ' ')}.`]),
    ) as Record<(typeof ABSATZ_KEYS)[number], string>,
  }
  await speichereErstberichtEntwurf(karte.coachingId, bearbeitung, karte.identity)
  await bestaetigeErstbericht(karte.coachingId, bearbeitung, karte.identity)
}

async function teilnahmeVon(coachingId: string) {
  const teilnahme = await apiFirst('/teilnahme', { coaching_id: coachingId, include: ['aufgabe'], limit: 1 })
  if (!teilnahme) throw new Error('Die Teilnahme der Karte ist nicht lesbar.')
  return teilnahme
}

async function abschliessenAlsKarte(karte: Karte): Promise<void> {
  const ziel = (await apiListAll('/aufgabe')).find((a) => a.name === AUFGABE.abgeschlossen.dbName)
  if (!ziel) throw new Error(`Aufgabe „${AUFGABE.abgeschlossen.dbName}“ ist nicht angelegt.`)
  const teilnahme = await teilnahmeVon(karte.coachingId)
  await apiPatch(
    '/teilnahme/{coaching_id}/{teilnehmende_id}',
    { coaching_id: karte.coachingId, teilnehmende_id: teilnahme.teilnehmende_id },
    { aufgabe_id: ziel.id },
    karte.identity,
  )
}

async function dokumentUnterschreiben(karte: Karte, z: Zubehoer, dokument: UnterschriftDokument, wer: Unterschreibende): Promise<void> {
  const signatur = await z.unterschrift()
  assertWriteIdentity(karte.identity)
  await unterschreibe(karte.coachingId, dokument, wer, signatur)
}

type Etappe = {
  schritt: AufgabeKey
  kurz: string
  was: string
  weg: ((karte: Karte, z: Zubehoer) => Promise<unknown>) | null
}

const ETAPPEN: readonly Etappe[] = [
  { schritt: 'verbisHochladen', kurz: 'VerBIS', was: 'Karte anlegen', weg: null },
  {
    schritt: 'eintrittsfragebogenAusfuellen',
    kurz: 'Eintritt',
    was: 'VerBIS überspringen',
    weg: (k) => verbisUeberspringen(k.coachingId),
  },
  {
    schritt: 'teilnehmerakteGegenzeichnen',
    kurz: 'Gegenzeichnung',
    was: 'Teilnehmerakte ausfüllen und unterschreiben',
    weg: teilnehmerakteAusfuellen,
  },
  {
    schritt: 'potenzialanalyseAusfuellen',
    kurz: 'Potenzialanalyse',
    was: 'Teilnehmerakte gegenzeichnen',
    weg: teilnehmerakteGegenzeichnen,
  },
  {
    schritt: 'klettHochladen',
    kurz: 'Klett',
    was: 'Aufnahme- und Potenzialanalyse speichern',
    weg: potenzialanalyseSpeichern,
  },
  {
    schritt: 'gevaHochladen',
    kurz: 'GEVA',
    was: 'Klett-Ergebnis hochladen',
    weg: async (k, z) => {
      const file = await z.klett()
      assertWriteIdentity(k.identity)
      await uploadKlett(k.coachingId, file, 'bestaetigt')
    },
  },
  {
    schritt: 'bewertungsbogenJobcoach',
    kurz: 'Jobcoach',
    was: 'GEVA-Test hochladen (die KI liest ihn)',
    weg: async (k, z) => {
      const file = await z.geva()
      assertWriteIdentity(k.identity)
      await uploadGeva(k.coachingId, file, 'bestaetigt')
    },
  },
  {
    schritt: 'bewertungsbogenFachanleiter',
    kurz: 'Fachanleiter',
    was: 'Bewertungsbogen Jobcoach ausfüllen',
    weg: bewertungsbogenSpeichern('jobcoach'),
  },
  {
    schritt: 'bewertungsbogenSprachlehrkraft',
    kurz: 'Sprachlehrkraft',
    was: 'Bewertungsbogen Fachanleiter ausfüllen',
    weg: bewertungsbogenSpeichern('anleiter'),
  },
  {
    schritt: 'kompetenzbilanzierung',
    kurz: 'Kompetenzbilanzierung',
    was: 'Bewertungsbogen Sprachlehrkraft ausfüllen',
    weg: bewertungsbogenSpeichern('sprachlehrkraft'),
  },
  {
    schritt: 'foerderplanErstellen',
    kurz: 'Förderplan',
    was: 'Kompetenzbilanzierung erzeugen und speichern (die KI schreibt sie)',
    weg: kompetenzbilanzierungSpeichern,
  },
  {
    schritt: 'foerderplanUnterschreiben',
    kurz: 'FP-Unterschrift',
    was: 'Förderplan erstellen',
    weg: foerderplanSpeichern,
  },
  {
    schritt: 'foerderplanGegenzeichnen',
    kurz: 'FP-Gegenzeichnung',
    was: 'Förderplan von der teilnehmenden Person unterschreiben',
    weg: async (k, z) => dokumentUnterschreiben(k, z, 'foerderplan', 'teilnehmende'),
  },
  {
    schritt: 'erstberichtErstellen',
    kurz: 'Erstbericht',
    was: 'Förderplan gegenzeichnen',
    weg: async (k, z) => dokumentUnterschreiben(k, z, 'foerderplan', 'coach'),
  },
  {
    schritt: 'erstberichtUnterschreiben',
    kurz: 'EB-Unterschrift',
    was: 'Erstbericht schreiben und bestätigen',
    weg: async (k) => erstberichtBestaetigen(k),
  },
  {
    schritt: 'erstberichtGegenzeichnen',
    kurz: 'EB-Gegenzeichnung',
    was: 'Erstbericht von der teilnehmenden Person unterschreiben',
    weg: async (k, z) => dokumentUnterschreiben(k, z, 'erstbericht', 'teilnehmende'),
  },
  {
    schritt: 'abgeschlossen',
    kurz: 'Abgeschlossen',
    was: 'Erstbericht gegenzeichnen, Teilnahme abschließen',
    weg: async (k, z) => {
      await dokumentUnterschreiben(k, z, 'erstbericht', 'coach')
      await abschliessenAlsKarte(k)
    },
  },
]

export const TESTDATEN_SCHRITTE: readonly { key: string; label: string }[] = ETAPPEN.map((e) => ({
  key: e.schritt,
  label: AUFGABE[e.schritt].dbName,
}))

export function testKundennummer(jetzt: number, nummer: number): string {
  return `TEST${jetzt.toString(36).toUpperCase()}${nummer}`
}

function isoTag(datum: Date): string {
  return datum.toISOString().slice(0, 10)
}

async function karteAnlegen(
  organisationId: string,
  kundennummer: string,
  vorname: string,
  nachname: string,
  identity: WriteIdentity,
): Promise<Karte> {
  const heute = new Date()
  const ende = new Date(heute)
  ende.setMonth(ende.getMonth() + 4)
  assertWriteIdentity(identity)
  const ergebnis = await anlegenTeilnehmendePerson({
    kundennummer,
    anrede: 'Frau',
    vorname,
    nachname,
    organisationszeichen: 'TEST',
    beginn: isoTag(heute),
    ende: isoTag(ende),
  })
  if (ergebnis.angelegt !== 1) throw new Error(`Kundennummer ${kundennummer} gibt es in dieser Organisation schon.`)
  const person = (await apiListAll('/teilnehmende', { organisation_id: organisationId })).find(
    (p) => p.kundennummer === kundennummer,
  )
  if (!person) throw new Error(`Die neue Person ${kundennummer} ist nicht lesbar.`)
  const teilnahme = await apiFirst('/teilnahme', { teilnehmende_id: person.id, limit: 1 })
  if (!teilnahme) throw new Error(`Die neue Person ${kundennummer} hat keine Teilnahme.`)
  assertWriteIdentity(identity)
  return { coachingId: teilnahme.coaching_id, vorname, nachname, identity }
}

export async function testdatenAnlegen(
  schritt: string,
  anzahl: number,
  melde: (fortschritt: string) => void,
  identity: WriteIdentity = captureWriteIdentity(),
): Promise<void> {
  assertWriteIdentity(identity)
  if (!Number.isInteger(anzahl) || anzahl < 1 || anzahl > 10) throw new Error('Bitte eine Zahl von 1 bis 10.')
  const ziel = ETAPPEN.findIndex((e) => e.schritt === schritt)
  if (ziel === -1) throw new Error(`Unbekannter Schritt „${schritt}“.`)
  const { organisationId, isOwner } = await me()
  assertWriteIdentity(identity)
  if (!isOwner) throw new Error('Nur die Organisationsverwaltung darf Testdaten anlegen.')
  const z = zubehoer(identity)
  const jetzt = Date.now()
  for (let nummer = 1; nummer <= anzahl; nummer++) {
    const vorname = 'Test'
    const nachname = `${ETAPPEN[ziel].kurz} ${nummer}`
    const wer = `Teilnehmende ${nummer} von ${anzahl} („${vorname} ${nachname}“)`
    let karte: Karte | null = null
    for (const etappe of ETAPPEN.slice(0, ziel + 1)) {
      assertWriteIdentity(identity)
      melde(`${wer}: ${etappe.was} …`)
      try {
        if (etappe.weg === null || karte === null) {
          karte = await karteAnlegen(organisationId, testKundennummer(jetzt, nummer), vorname, nachname, identity)
        } else {
          await etappe.weg(karte, z)
        }
        const steht = (await teilnahmeVon(karte.coachingId)).aufgabe?.name ?? '-'
        assertWriteIdentity(identity)
        const soll = AUFGABE[etappe.schritt].dbName
        if (steht !== soll) throw new Error(`Die Karte steht auf „${steht}“ statt auf „${soll}“.`)
      } catch (error) {
        const fertig = nummer - 1
        throw new Error(
          `${wer}, Schritt „${etappe.was}“: ${friendlyErrorMessage(error, 'Die Aktion konnte nicht abgeschlossen werden.')} ` +
            `Vollständig angelegt: ${fertig} Teilnehmende.`,
          { cause: error },
        )
      }
    }
  }
}
